/**
 * API service for Media Library operations.
 */
import { apiClient } from './client';
import type {
  MediaFolder,
  MediaTag,
  MediaImage,
  ImageUsage,
  StorageSummary,
  CreateFolderRequest,
  UpdateFolderRequest,
  CreateTagRequest,
  UpdateImageRequest,
  ListImagesRequest,
  ListImagesResponse,
  BatchDeleteRequest,
  BatchMoveRequest,
  BatchTagRequest,
  BatchOperationResponse,
  UploadImageResponse,
  CropBox,
  ImageCategory,
} from '@/types/media';

// =============================================================================
// Folder Operations
// =============================================================================

export async function listFolders(memberUuid: string): Promise<MediaFolder[]> {
  const response = await apiClient.get<MediaFolder[]>(`/media/folders/${memberUuid}`);
  return response.data;
}

export async function createFolder(data: CreateFolderRequest): Promise<MediaFolder> {
  const response = await apiClient.post<MediaFolder>('/media/folders', data);
  return response.data;
}

export async function updateFolder(
  folderId: string,
  data: UpdateFolderRequest
): Promise<MediaFolder> {
  const response = await apiClient.put<MediaFolder>(`/media/folders/${folderId}`, data);
  return response.data;
}

export async function deleteFolder(
  folderId: string,
  memberUuid: string
): Promise<void> {
  await apiClient.delete(`/media/folders/${folderId}`, {
    params: { member_uuid: memberUuid },
  });
}

// =============================================================================
// Tag Operations
// =============================================================================

export async function listTags(memberUuid: string): Promise<MediaTag[]> {
  const response = await apiClient.get<MediaTag[]>(`/media/tags/${memberUuid}`);
  return response.data;
}

export async function createTag(data: CreateTagRequest): Promise<MediaTag> {
  const response = await apiClient.post<MediaTag>('/media/tags', data);
  return response.data;
}

export async function deleteTag(tagId: string, memberUuid: string): Promise<void> {
  await apiClient.delete(`/media/tags/${tagId}`, {
    params: { member_uuid: memberUuid },
  });
}

// =============================================================================
// Image Operations
// =============================================================================

export async function uploadImage(
  file: File,
  memberUuid: string,
  options?: {
    folderId?: string;
    altText?: string;
    description?: string;
    tagIds?: string[];
    category?: Exclude<ImageCategory, 'targeted'>;
  }
): Promise<UploadImageResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('member_uuid', memberUuid);

  if (options?.folderId) {
    formData.append('folder_id', options.folderId);
  }
  if (options?.altText) {
    formData.append('alt_text', options.altText);
  }
  if (options?.description) {
    formData.append('description', options.description);
  }
  if (options?.tagIds && options.tagIds.length > 0) {
    formData.append('tag_ids', JSON.stringify(options.tagIds));
  }
  if (options?.category) {
    formData.append('category', options.category);
  }

  const response = await apiClient.post<UploadImageResponse>(
    '/media/images/upload',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 1 minute for upload
    }
  );
  return response.data;
}

export async function listImages(
  params: ListImagesRequest
): Promise<ListImagesResponse> {
  const response = await apiClient.post<ListImagesResponse>('/media/images/list', params);
  return response.data;
}

export async function getImage(
  imageId: string,
  memberUuid: string
): Promise<MediaImage> {
  const response = await apiClient.get<MediaImage>(`/media/images/${imageId}`, {
    params: { member_uuid: memberUuid },
  });
  return response.data;
}

export async function updateImage(
  imageId: string,
  data: UpdateImageRequest
): Promise<MediaImage> {
  const response = await apiClient.put<MediaImage>(`/media/images/${imageId}`, data);
  return response.data;
}

export async function deleteImage(
  imageId: string,
  memberUuid: string
): Promise<void> {
  await apiClient.delete(`/media/images/${imageId}`, {
    params: { member_uuid: memberUuid },
  });
}

// =============================================================================
// Batch Operations
// =============================================================================

export async function batchDeleteImages(
  data: BatchDeleteRequest
): Promise<BatchOperationResponse> {
  const response = await apiClient.post<BatchOperationResponse>(
    '/media/images/batch-delete',
    data
  );
  return response.data;
}

export async function batchMoveImages(
  data: BatchMoveRequest
): Promise<BatchOperationResponse> {
  const response = await apiClient.post<BatchOperationResponse>(
    '/media/images/batch-move',
    data
  );
  return response.data;
}

export async function batchTagImages(
  data: BatchTagRequest
): Promise<BatchOperationResponse> {
  const response = await apiClient.post<BatchOperationResponse>(
    '/media/images/batch-tag',
    data
  );
  return response.data;
}

// =============================================================================
// Storage Summary
// =============================================================================

export async function getStorageSummary(memberUuid: string): Promise<StorageSummary> {
  const response = await apiClient.get<StorageSummary>(`/media/storage/${memberUuid}`);
  return response.data;
}

// =============================================================================
// Usage Tracking Operations
// =============================================================================

export interface LinkImageRequest {
  member_uuid: string;
  usage_type: 'campaign' | 'ad_group' | 'asset_group' | 'ad';
  campaign_id?: string;
  campaign_name?: string;
  ad_group_id?: string;
  asset_group_id?: string;
  ad_id?: string;
  google_ads_account_id?: string;
}

export interface GetImagesByEntityRequest {
  member_uuid: string;
  usage_type: 'campaign' | 'ad_group' | 'asset_group' | 'ad';
  campaign_id?: string;
  ad_group_id?: string;
  asset_group_id?: string;
  ad_id?: string;
  google_ads_account_id?: string;
}

export interface ImagesByEntityResponse {
  images: MediaImage[];
  total: number;
}

export async function linkImageToEntity(
  imageId: string,
  data: LinkImageRequest
): Promise<ImageUsage> {
  const response = await apiClient.post<ImageUsage>(`/media/images/${imageId}/link`, data);
  return response.data;
}

export async function unlinkImageFromEntity(
  imageId: string,
  usageId: string,
  memberUuid: string
): Promise<void> {
  await apiClient.delete(`/media/images/${imageId}/link/${usageId}`, {
    params: { member_uuid: memberUuid },
  });
}

export async function getImagesByEntity(
  data: GetImagesByEntityRequest
): Promise<ImagesByEntityResponse> {
  const response = await apiClient.post<ImagesByEntityResponse>('/media/images/by-entity', data);
  return response.data;
}

// =============================================================================
// Categories + Targeted images
// =============================================================================

export interface SetImageCategoryRequest {
  member_uuid: string;
  /** Only 'reference' or 'approved' are accepted; Targeted is set-once. */
  category: Exclude<ImageCategory, 'targeted'>;
}

export async function setImageCategory(
  imageId: string,
  data: SetImageCategoryRequest
): Promise<MediaImage> {
  const response = await apiClient.post<MediaImage>(
    `/media/images/${imageId}/category`,
    data
  );
  return response.data;
}

export interface CreateTargetedImageRequest {
  member_uuid: string;
  /** e.g. 'google_ads.square_marketing_image' */
  placement_key: string;
  /** Omit to let the server smart-crop; provide for a manual user-confirmed crop. */
  crop_box?: CropBox;
}

/**
 * Create (or return the existing) Targeted derivative of an Approved source image.
 * Backend dedupes on (source, placement, crop_box) so picking the same crop twice
 * is idempotent.
 */
export async function createTargetedImage(
  sourceImageId: string,
  data: CreateTargetedImageRequest
): Promise<MediaImage> {
  const response = await apiClient.post<MediaImage>(
    `/media/images/${sourceImageId}/targeted`,
    data
  );
  return response.data;
}
